import { useEffect, useState } from "react"
import QRCode from "qrcode"
import rustoreIcon from "../assets/icons/rustore.svg"
import PwaInstallBadge from "./PwaInstallBadge"

const DISPLAY = "'Tilda Sans', sans-serif"
const MONO = "'JetBrains Mono', monospace"

const STORES = [
    {
        href: "https://apps.apple.com/ru/app/%D0%BC%D1%8F%D0%B3%D0%BA%D0%B8%D0%B9-%D0%BA%D0%B8%D0%BD%D0%BE%D1%82%D0%B5%D0%B0%D1%82%D1%80/id1484484733",
        name: "App Store",
        icon: (size: number) => (
            <svg width={size} height={size * 1.2} viewBox="0 0 814 1000" fill="white">
                <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.5-155.5-127.8c-42.3-77.1-85.7-193.3-85.7-304.6 0-201.9 131.9-308.5 261.7-308.5 69.4 0 127.7 45.3 171.5 45.3 42.5 0 109.2-48 185.8-48l33.8 2.3zm-24.5-143.7c26.7-31.5 45.7-75.8 45.7-120.1 0-6.4-.6-12.8-1.9-18.5-43.3 1.6-94.5 28.5-125.4 64.7-23.1 25.9-45.8 69.5-45.8 114.4 0 7 1.3 13.9 1.9 16.2 2.6.3 6.5.6 10.3.6 38.7 0 86.8-25.3 115.2-57.3z" />
            </svg>
        ),
    },
    {
        href: "https://play.google.com/store/apps/details?id=ru.comfortkino.androidapp",
        name: "Google Play",
        icon: (size: number) => (
            <svg width={size} height={size * 1.1} viewBox="0 0 512 512" fill="none">
                <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l232.6-233-232.6-232.9zm425.6 225.6l-58.9-34-67.7 68 67.7 68 59.1-34c16.4-9.2 16.4-34.9-.2-48zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" fill="white" />
            </svg>
        ),
    },
    {
        href: "https://www.rustore.ru/catalog/app/ru.comfortkino.androidapp",
        name: "RuStore",
        icon: (size: number) => (
            <img src={rustoreIcon} alt="" width={size} height={size} className="shrink-0" style={{ width: size, height: size }} />
        ),
    },
] as const

/* QR со ссылкой на магазин — всплывает при наведении на бейдж (только устройства с курсором):
   с ПК переходить по ссылке магазина бессмысленно, проще навести камеру телефона */
function StoreQr({ url, name }: { url: string; name: string }) {
    const [dataUrl, setDataUrl] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false
        /* width 264 (2x от 132px) — чётко на retina */
        QRCode.toDataURL(url, { margin: 1, width: 264, color: { dark: "#18133b", light: "#ffffff" } })
            .then(d => {
                if (!cancelled) setDataUrl(d)
            })
            .catch(() => {})
        return () => {
            cancelled = true
        }
    }, [url])

    if (!dataUrl) return null

    return (
        <div className="pointer-events-none absolute bottom-full left-1/2 z-30 hidden -translate-x-1/2 flex-col items-center pb-2 pointer-fine:group-hover:flex">
            <div
                className="flex flex-col items-center gap-2 rounded-[16px] p-3 shadow-[0_12px_40px_rgba(0,0,0,0.5)]"
                style={{
                    background: "linear-gradient(160deg, #1a2d55 0%, #18133b 100%)",
                    border: "1.5px solid rgba(255,255,255,0.14)",
                }}>
                <img src={dataUrl} alt={`QR-код: ${name}`} width={132} height={132} className="rounded-[8px]" />
                <span className="text-[10px] whitespace-nowrap text-white/55" style={{ fontFamily: MONO, fontWeight: 600 }}>
                    Наведите камеру телефона
                </span>
            </div>
            {/* Стрелочка вниз к бейджу */}
            <div
                className="-mt-px size-2.5 rotate-45"
                style={{ background: "#18133b", borderRight: "1.5px solid rgba(255,255,255,0.14)", borderBottom: "1.5px solid rgba(255,255,255,0.14)" }}
            />
        </div>
    )
}

export type AppStoreBadgesProps = {
    /** default — крупные бейджи лендинга, compact — сдержанный вариант для подвала */
    variant?: "default" | "compact"
    className?: string
}

export default function AppStoreBadges({ variant = "default", className = "" }: AppStoreBadgesProps) {
    const compact = variant === "compact"
    const iconSize = compact ? 15 : 20
    const linkClass = compact
        ? "flex items-center gap-2.5 rounded-xl border border-white/12 bg-white/[0.04] px-3.5 py-2 hover:bg-white/[0.09] hover:border-white/30 transition-all lg:px-5"
        : "flex items-center gap-3 rounded-2xl border border-white/20 bg-white/[0.06] px-5 py-3 hover:bg-white/[0.12] hover:border-white/40 transition-all"

    return (
        <div className={`flex flex-wrap gap-3 ${className}`}>
            {STORES.map(({ href, name, icon }) => (
                <a key={name} href={href} target="_blank" rel="noreferrer" className={`group relative ${linkClass}`}>
                    <StoreQr url={href} name={name} />
                    {icon(iconSize)}
                    <div>
                        <div
                            className={compact ? "text-[9px] text-white/45 lg:text-[10px]" : "text-[10px] text-white/50"}
                            style={{ fontFamily: MONO, fontWeight: 600 }}>
                            Загрузить в
                        </div>
                        <div
                            className={compact ? "text-[12px] text-white font-bold lg:text-[14px]" : "text-[14px] text-white font-bold"}
                            style={{ fontFamily: DISPLAY }}>
                            {name}
                        </div>
                    </div>
                </a>
            ))}
            {/* Установка PWA — там, где браузер это умеет (Android/Chrome, iOS Safari) */}
            <PwaInstallBadge compact={compact} />
        </div>
    )
}
